import axios from 'axios';
import {
    isTokenExpired,
    logTokenInfo,
    getTokenRemainingTime,
} from './utils/jwtUtils';

// Extend Axios config to include custom property
declare module 'axios' {
    export interface AxiosRequestConfig {
        skipAuthRedirect?: boolean;
    }
}

export const axiosInstance = axios.create({
    headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'Access-Control-Allow-Origin': '*',
    },
    baseURL: import.meta.env.VITE_API_V1_URL,
    // other configurations
});

// Add a request interceptor to include the token in the headers
axiosInstance.interceptors.request.use(
    (config) => {
        const tokensStr = sessionStorage.getItem('tokens');
        const token = tokensStr ? JSON.parse(tokensStr)?.token : null;

        // users, tokens, roles and inventory endpoints use non-versioned base URL
        // Note: Use startsWith for /inventory to avoid matching /storagelocation/{id}/inventory
        if (
            config.url.includes('/users') ||
            config.url.includes('/tokens') ||
            config.url.includes('/roles') ||
            config.url.startsWith('/inventory')
        ) {
            config.baseURL = import.meta.env.VITE_NOVERSION_API_URL;
        }

        if (token) {
            // Check if token is expired or about to expire
            if (isTokenExpired(token, 60)) {
                // 60 second buffer
                const remainingTime = getTokenRemainingTime(token);
                console.warn(
                    `JWT token is expired or expiring soon (${remainingTime}s remaining) for request:`,
                    config.url
                );
                logTokenInfo(token, 'Expired/Expiring Token');
            }

            config.headers['Authorization'] = `Bearer ${token}`;
        } else {
            console.warn(
                'No JWT token found in session storage for request:',
                config.url
            );
        }

        return config;
    },
    (error) => {
        console.error('Request interceptor error:', error);
        return Promise.reject(error);
    }
);

// Prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let failedQueue: Array<{
    resolve: (value?: any) => void;
    reject: (reason?: any) => void;
}> = [];

const processQueue = (error: any, token: string | null = null) => {
    failedQueue.forEach(({ resolve, reject }) => {
        if (error) {
            reject(error);
        } else {
            resolve(token);
        }
    });

    failedQueue = [];
};

// Add a response interceptor to handle 401 errors and refresh the token
axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        // If the error is due to invalid credentials
        if (
            error.response &&
            error.response.status === 401 &&
            error.response.data === 'Username and password is not matching'
        ) {
            return Promise.resolve(error);
        }

        // Skip token refresh and logout for requests marked with skipAuthRedirect
        // This is used for endpoints that authenticate with direct credentials
        // (e.g., signing, double-signing) where 401 means wrong credentials, not expired session
        if (originalRequest.skipAuthRedirect) {
            return Promise.reject(error);
        }

        // If the error is due to invalid or expired JWT
        // Double-check skipAuthRedirect to ensure we don't logout for endpoints that handle auth differently
        if (
            error.response &&
            error.response.status === 401 &&
            !originalRequest._retry &&
            !originalRequest.skipAuthRedirect
        ) {
            console.log(
                'JWT expired, attempting token refresh',
                error.response
            );

            // Log current token info for debugging
            const currentTokens = JSON.parse(
                sessionStorage.getItem('tokens') || '{}'
            );
            if (currentTokens.token) {
                logTokenInfo(currentTokens.token, 'Expired Token');
            }

            if (isRefreshing) {
                // If already refreshing, queue this request
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                })
                    .then((token) => {
                        originalRequest.headers['Authorization'] =
                            `Bearer ${token}`;
                        return axiosInstance(originalRequest);
                    })
                    .catch((err) => {
                        return Promise.reject(err);
                    });
            }

            originalRequest._retry = true;
            isRefreshing = true;

            const tokensStr = sessionStorage.getItem('tokens');
            if (!tokensStr) {
                processQueue(error, null);
                isRefreshing = false;
                sessionStorage.removeItem('tokens');
                window.location.href = '/login?sessionExpired=true';
                return Promise.reject(error);
            }

            const tokens = JSON.parse(tokensStr);
            const token = tokens?.token;
            const refreshToken = tokens?.refreshToken;

            // Get tenant from localStorage (same as login flow)
            const tenant = localStorage.getItem('orgId')
                ? JSON.parse(localStorage.getItem('orgId')!)
                : null;

            if (token && refreshToken) {
                try {
                    const response = await axios.post(
                        `${import.meta.env.VITE_NOVERSION_API_URL}/tokens/refresh`,
                        {
                            token: token,
                            refreshToken: refreshToken,
                        },
                        { headers: { tenant: tenant } }
                    );

                    const newToken = response.data.token;
                    const newRefreshToken =
                        response.data.refreshToken || refreshToken; // Use new refresh token if provided, otherwise keep old one

                    console.log('Token refresh successful');

                    // Log new token info for debugging
                    logTokenInfo(newToken, 'New Token');

                    // Update session storage with both new token and refresh token
                    sessionStorage.setItem(
                        'tokens',
                        JSON.stringify({
                            token: newToken,
                            refreshToken: newRefreshToken,
                        })
                    );

                    // Update axios default headers
                    axiosInstance.defaults.headers.common['Authorization'] =
                        `Bearer ${newToken}`;
                    originalRequest.headers['Authorization'] =
                        `Bearer ${newToken}`;

                    // Process queued requests
                    processQueue(null, newToken);
                    isRefreshing = false;

                    return axiosInstance(originalRequest);
                } catch (refreshError) {
                    console.error('Token refresh failed:', refreshError);

                    // Process queued requests with error
                    processQueue(refreshError, null);
                    isRefreshing = false;

                    // Handle refresh token failure, e.g., redirect to login
                    sessionStorage.removeItem('tokens');
                    window.location.href = '/login?sessionExpired=true';
                    return Promise.reject(refreshError);
                }
            } else {
                console.error('No valid tokens found for refresh');

                // Process queued requests with error
                processQueue(error, null);
                isRefreshing = false;

                // No refresh token available, redirect to login
                sessionStorage.removeItem('tokens');
                window.location.href = '/login?sessionExpired=true';
                return Promise.reject(error);
            }
        }
        return Promise.reject(error);
    }
);
